import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Run } from "@quantizr-engineering/common/types/run";
import CreateShowModal from "../Shows/CreateShowModal";
import {
  ArtistExpenseTag,
  Show,
} from "@quantizr-engineering/common/types/show";
import { useRunService } from "../../api/runService";
import { useShowService } from "../../api/showService";
import Loader from "../../components/Loader";
import { PiSparkle } from "react-icons/pi";
import {
  showsForRun,
  ShowStatus,
} from "@quantizr-engineering/common/types/show";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { FiChevronDown, FiPlus } from "react-icons/fi";
import FinancesTable from "../../components/FinancesTable";
import { FieldInputProps, Field, FormikProps } from "formik";
import StatusStepper from "../../components/StatusStepper";
import { getRunDates } from "../../utils/dateFormatters.ts";
import { prettyDate } from "../../utils/prettyDate";
import { useAiService } from "../../api/askQz";
import { AskQzModal } from "../../components/AskQzModal";
import {
  calculateRunFigures,
  calculateShowFigures,
  calculateTicketsSoldForecast,
  calculateSellableCap,
} from "@quantizr-engineering/common/math/live";
import { enumToOptions } from "../../utils/enumToOptions";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import DocumentsTable from "../../components/DocumentsTable";
import TextCell from "../../components/TextCell.tsx";
import { ModalPortal } from "../../components/ModalPortal.tsx";
import {
  getStatusStyling,
  isShowInActualStatus,
} from "../../utils/showStatus.ts";
import { formatCurrency, formatNumber } from "../../utils/numberFormatters.tsx";
import SummaryTable from "../../components/SummaryTable";
import { useRunDetailsCalculations } from "../../utils/useRunDetailsCalculations.tsx";
import ReusableFormikWrapper from "../../contexts/ReusableFormikWrapper.tsx";
import { FaCopy, FaTrash } from "react-icons/fa";
import { toast } from "sonner";
import DropdownMenuWithHeroButton from "../../components/DropdownMenuWithHeroButton.tsx";
import TakeHomeCard from "../../components/TakeHomeCard.tsx";
import ProjectedRunMarginCard from "../../components/ProjectedRunMarginCard.tsx";
import EmblaCarousel from "../../components/EmblaCarousel.tsx";
import { expenseValidationSchema } from "../../validation/schemas/expenseValidationSchema.ts";
import file06 from "../../assets/file06.svg";

interface RunDetailsProps {}
interface AskQzState {
  message: string;
  response: string;
  loading: boolean;
  modalOpen: boolean;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const TABS = [
  { id: "shows", label: "Shows" },
  { id: "runFinances", label: "Run Finances" },
  { id: "runDocuments", label: "Run Documents" },
];
const TABLE_HEADERS = ["Date", "Name", "Status", "Sold/Cap", "Artist Walkout"];
const TABLE_HEADER_STYLES =
  "px-3 text-left text-sm font-semibold text-black tracking-wider";

const getStatusBackgroundColor = (status: ShowStatus): string => {
  const bgColors = {
    [ShowStatus.DRAFT]: "bg-[#E9E8ED]",
    [ShowStatus.CONFIRMED]: "bg-[#F1E5FF]",
    [ShowStatus.SETTLED]: "bg-[#E2F4FC]",
    [ShowStatus.PAID]: "bg-[#D1FAE2]",
  };
  return bgColors[status] ?? "bg-[#E9E8ED]";
};

const getStatusTextColor = (status: ShowStatus): string => {
  const textColors = {
    [ShowStatus.DRAFT]: "text-[#48455B]",
    [ShowStatus.CONFIRMED]: "text-[#4932F5]",
    [ShowStatus.SETTLED]: "text-[#0D5F7F]",
    [ShowStatus.PAID]: "text-[#054F36]",
  };
  return textColors[status] ?? "text-[#48455B]";
};

const createGradient = (
  ctx: CanvasRenderingContext2D,
  chartArea: any,
  color: string
) => {
  const gradient = ctx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom
  );
  gradient.addColorStop(0, `${color}66`);
  gradient.addColorStop(1, `${color}00`);
  return gradient;
};

export const RunDetails: React.FC<RunDetailsProps> = () => {
  const { id } = useParams<{ id: string }>();
  const { shows, showsLoading } = useShowService();
  const { runs, patchRun, deleteRun, copyRun, generateRunForecastReport } =
    useRunService();
  const { generateQzResponse } = useAiService();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] =
    useState<(typeof TABS)[number]["id"]>("shows");
  const [showCreateShowModal, setShowCreateShowModal] =
    useState<boolean>(false);
  const [askQz, setAskQz] = useState<AskQzState>({
    message: "",
    response: "",
    loading: false,
    modalOpen: false,
  });

  const handleAskQz = async () => {
    setAskQz((prev) => ({ ...prev, loading: true }));
    try {
      const response = await generateQzResponse(
        askQz.message,
        selectedRun,
        selectedShows
      );
      setAskQz((prev) => ({ ...prev, response }));
    } finally {
      setAskQz((prev) => ({ ...prev, loading: false }));
    }
  };

  const selectedRun = useMemo<Run | null>(
    () =>
      Array.isArray(runs)
        ? runs.find((run) => run.id === parseInt(id || "")) || null
        : null,
    [runs, id]
  );

  const memoizedRunDates = useMemo<string>(() => {
    if (selectedRun && shows) {
      return getRunDates(selectedRun, shows);
    }
    return "No dates available";
  }, [selectedRun, shows]);

  const selectedShows = useMemo<Show[]>(() => {
    if (!selectedRun) return [];
    return showsForRun(shows, selectedRun).sort(
      (a, b) => a.performanceDate.getTime() - b.performanceDate.getTime()
    );
  }, [shows, selectedRun]);

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const label = context.dataset.label || "";
            let value = context.parsed.y;
            // Format differently based on dataset
            if (label === "Walkout") {
              return `${label}: ${formatCurrency(value)}`;
            } else {
              return `${label}: ${value.toLocaleString()}`;
            }
          },
        },
      },
      datalabels: {
        display: () => false,
      },
    },
    elements: {
      line: {
        tension: 0,
      },
      point: {
        radius: 5,
        hoverRadius: 6,
      },
    },
    layout: {
      padding: {
        bottom: 30,
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        type: "linear",
        position: "left",
        title: {
          display: false,
          text: "Walkout",
          color: "#35C1ED",
        },
        beginAtZero: true,
        ticks: {
          color: "#35C1ED",
          callback: function (value: number | string) {
            return typeof value === "number" ? formatCurrency(value) : value;
          },
        },
      },
      y1: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        type: "linear",
        position: "right",
        beginAtZero: true,
        title: {
          display: false,
          text: "Tickets Sold",
          color: "#8B5CF6",
        },
        ticks: {
          color: "#8B5CF6",
          callback: function (value: number | string) {
            return typeof value === "number" ? value.toLocaleString() : value;
          },
        },
      },
    },
  };
  const chartData = {
    labels: selectedShows.map((show) => show.venue),
    datasets: [
      {
        label: "Walkout",
        data: showsForRun(selectedShows, selectedRun).map((show) =>
          show.status === ShowStatus.SETTLED || ShowStatus.PAID
            ? calculateShowFigures(show).walkout
            : calculateShowFigures(show).walkoutProjected
        ),
        yAxisID: "y",
        borderColor: "#35C1ED",
        backgroundColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return;
          return createGradient(ctx, chartArea, "#35C1ED");
        },
        fill: true,
        tension: 0,
        pointRadius: 2,
        borderWidth: 2,
        pointBackgroundColor: "#35C1ED",
        pointBorderColor: "#35C1ED",
      },
      {
        label: "Tickets Sold",
        data: showsForRun(selectedShows, selectedRun).map((show) =>
          show.status === ShowStatus.SETTLED || ShowStatus.PAID
            ? calculateShowFigures(show).ticketsSoldActual
            : calculateShowFigures(show).ticketsSoldProjected
        ),
        yAxisID: "y1",
        borderColor: "#8B5CF6",
        backgroundColor: (context: any) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return;
          return createGradient(ctx, chartArea, "#8B5CF6");
        },
        fill: true,
        tension: 0,
        pointRadius: 2,
        borderWidth: 2,
        pointBackgroundColor: "#8B5CF6",
        pointBorderColor: "#8B5CF6",
      },
    ],
  };

  // const AskQzCard1 = () => {
  //   return (
  //     <div className="w-full h-full bg-white text-black p-6 relative">
  //       <div className="card-body p-0 flex flex-col h-full">
  //         <div className="text-2xl">
  //           <PiSparkle />
  //         </div>
  //         <div className="flex flex-col">
  //           <span className="text-sm text-gray-700">
  //             {selectedRun?.venue || "Show name"}
  //           </span>
  //           <div className="text-xl font-semibold leading-snug">
  //             This show has a budgeted net box office receipts of $165,850
  //           </div>
  //         </div>
  //         <div className="mt-1">
  //           <button
  //             type="button"
  //             className="btn btn-sm bg-gray-100 hover:bg-gray-300 text-black rounded-full px-2 py-1 flex items-center gap-2 font-semibold hover:shadow-md transition border-none"
  //           >
  //             View show
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // };

  const AskQzCard2 = () => {
    return (
      <div className="w-full h-full bg-white bg-[linear-gradient(to_top_right,_var(--tw-gradient-stops))] from-custom-teal via-custom-dark-purple to-custom-purple text-white p-6 relative">
        <div className="card-body p-0 flex flex-col h-full">
          <div className="text-2xl">
            <PiSparkle />
          </div>
          <div className="text-xl font-semibold leading-snug">
            Want more insights or clarity on your data? <br /> Ask Qz.
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setAskQz((prev) => ({ ...prev, modalOpen: true }))}
              className="btn rounded-full flex items-center justify-center font-bold bg-white text-black border-none"
            >
              <PiSparkle />

              <span className="text-center text-sm md:text-base">Ask Qz</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const carouselItems1 = [
    <div>
      <TakeHomeCard
        label="Potential Take-Home"
        amount={
          calculateRunFigures(selectedRun, selectedShows)
            .artistTakeHomePotential
        }
        runScore={undefined}
        status={"Potential"}
      />
    </div>,

    <div>
      <TakeHomeCard
        label="Projected Take-Home"
        amount={
          calculateRunFigures(selectedRun, selectedShows)
            .artistTakeHomeProjected
        }
        runScore={
          calculateRunFigures(selectedRun, selectedShows).marginProjected
        }
        status={"Projected"}
      />
    </div>,

    <div>
      <TakeHomeCard
        label="Forecasted Take-Home"
        amount={
          calculateRunFigures(selectedRun, selectedShows).artistTakeHomeForecast
        }
        runScore={
          calculateRunFigures(selectedRun, selectedShows).marginForecast
        }
        status={"Forecasted"}
      />
    </div>,

    <div className="flex flex-col h-full">
      <TakeHomeCard
        label="Actual Take-Home"
        amount={
          calculateRunFigures(selectedRun, selectedShows).artistTakeHomeActual
        }
        runScore={calculateRunFigures(selectedRun, selectedShows).marginActual}
        status={"Actual"}
      />
    </div>,
  ];

  const carouselItems3 = [<AskQzCard2 />];

  const getValidationSchema = (activeTab: string) => {
    if (activeTab !== "runFinances") return null;

    return Yup.object().shape({
      crew: Yup.array().of(expenseValidationSchema),
      expenses: Yup.array().of(expenseValidationSchema),
    });
  };

  if (showsLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (!selectedRun) {
    return <div>Run not found</div>;
  }

  return (
    <ReusableFormikWrapper
      validationSchema={getValidationSchema(activeTab)}
      data={{
        ...selectedRun,
        crew: selectedRun.expenses
          ?.filter((e) => e.tag === "Crew")
          .sort((a, b) => a.name.localeCompare(b.name)),
        expenses: selectedRun.expenses
          ?.filter((e) => e.tag !== "Crew")
          .sort((a, b) => a.name.localeCompare(b.name)),
      }}
      onSubmitHandler={async (
        values,
        { setValues, setSubmitting, resetForm }
      ) => {
        const updatedValues = { ...values };

        const updatedValuesCopy = Object.assign({}, updatedValues);
        resetForm({ values: updatedValuesCopy });
        updatedValues.expenses = (values.expenses ?? []).concat(
          values.crew ?? []
        );

        try {
          await patchRun(updatedValues);
        } catch (error) {
          console.error("Error updating show:", error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, handleBlur, handleKeyDown }) => {
        const { runValues, runDetailsAccordions } = useRunDetailsCalculations(
          { ...values, expenses: [...values.expenses, ...values.crew] },
          selectedShows
        );
        return (
          <div className="flex flex-col bg-white h-screen max-w-full">
            {/* Sticky TOP Section */}
            <div className="sticky top-0 z-20 bg-white">
              <div className="px-4 pt-2">
                <Breadcrumbs
                  items={[
                    { text: "Live", link: "/runs" },
                    { text: selectedRun.name },
                  ]}
                />
              </div>

              <header className="flex justify-between items-center px-4 pt-1 shadow-sm">
                <div className="flex flex-col w-[50%]">
                  <Field name="name">
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<number>;
                      form: FormikProps<any>;
                    }) => (
                      <div
                        className="text-3xl font-bold text-black"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (form.dirty) {
                              form.handleSubmit();
                            }
                          }
                        }}
                      >
                        <TextCell {...field} onBlur={handleBlur} />
                      </div>
                    )}
                  </Field>
                  <div className="text-sm text-[#48455B] pb-2 pt-2">
                    {memoizedRunDates}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={() =>
                      setAskQz((prev) => ({ ...prev, modalOpen: true }))
                    }
                    className="btn w-full md:w-auto rounded-full flex items-center justify-center font-bold px-4 py-2 min-w-[180px] max-w-[180px]"
                  >
                    <span className="flex-shrink-0 mr-2">
                      <PiSparkle />
                    </span>
                    <span className="text-center text-sm md:text-base">
                      Ask Qz
                    </span>
                  </button>
                  <DropdownMenuWithHeroButton
                    rightIcon={<FiChevronDown className="text-lg" />}
                    buttonText="Actions"
                    items={[
                      {
                        icon: <FiPlus className="text-lg" />,
                        text: "New Show",
                        onClick: () => setShowCreateShowModal(true),
                      },
                      {
                        icon: <FaTrash className="text-lg" />,
                        text: "Delete This Run",
                        onClick: () => {
                          toast("Delete this run?", {
                            duration: 10000,
                            action: {
                              label: "Confirm",
                              onClick: async () => {
                                await deleteRun(selectedRun);
                                navigate(`/runs/`);
                                toast.success("Run deleted!");
                              },
                            },
                            cancel: {
                              label: "Cancel",
                              onClick: () => {},
                            },
                          });
                        },
                      },
                      {
                        icon: <FaCopy className="text-lg" />,
                        text: "Copy Run",
                        onClick: () => {
                          toast("Copy this run?", {
                            duration: 10000,
                            action: {
                              label: "Confirm",
                              onClick: async () => {
                                await copyRun(selectedRun);
                                navigate(`/runs/`);
                                toast.success("Run copied!");
                              },
                            },
                            cancel: {
                              label: "Cancel",
                              onClick: () => {},
                            },
                          });
                        },
                      },
                      {
                        icon: (
                          <img
                            src={file06}
                            alt="Send Run Report"
                            className="w-5 h-5"
                          />
                        ),
                        text: "Send Run Report",
                        onClick: () => {
                          toast(
                            <div className="flex flex-col gap-2">
                              <span>Email To Send To?</span>
                              <input
                                type="email"
                                className="px-2 py-1 border rounded text-black"
                                placeholder="Enter email address"
                                id="settlement-email-input"
                                autoFocus
                              />
                            </div>,
                            {
                              duration: 30000,
                              action: {
                                label: "Send",
                                onClick: async () => {
                                  const emailInput = document.getElementById(
                                    "settlement-email-input"
                                  ) as HTMLInputElement;
                                  const emailAddress = emailInput?.value;
                                  if (emailAddress) {
                                    toast.info(
                                      `Sending report to ${emailAddress}...`
                                    );
                                    generateRunForecastReport(
                                      selectedRun,
                                      emailAddress
                                    );
                                  } else {
                                    toast.error(
                                      "Please enter a valid email address"
                                    );
                                  }
                                },
                              },
                              cancel: {
                                label: "Cancel",
                                onClick: () => {},
                              },
                            }
                          );
                        },
                      },
                    ]}
                  />
                </div>
              </header>
            </div>

            {id && (
              <ModalPortal>
                {askQz.modalOpen === true && (
                  <AskQzModal
                    isOpen={askQz.modalOpen}
                    onClose={() =>
                      setAskQz((prev) => ({
                        ...prev,
                        modalOpen: false,
                        message: "",
                        response: "",
                      }))
                    }
                    message={askQz.message}
                    response={askQz.response}
                    loading={askQz.loading}
                    onMessageChange={(message) =>
                      setAskQz((prev) => ({ ...prev, message }))
                    }
                    onSubmit={handleAskQz}
                  />
                )}
                <CreateShowModal
                  isOpen={showCreateShowModal}
                  onClose={() => setShowCreateShowModal(false)}
                  runID={parseInt(id)}
                />
              </ModalPortal>
            )}

            {/* Scrollable Content Area - Only on sm/md screens */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden lg:overflow-visible">
              {/* Carousel + Chart Section */}
              <div className="grid gap-4 w-full px-4 py-2 grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-12">
                <div className="flex flex-col gap-2 h-[240px] sm:h-[240px] md:h-[240px] lg:col-span-3 lg:h-full lg:max-h-[240px]">
                  <EmblaCarousel slides={carouselItems1} startIndex={2} />
                </div>
                <div className="h-[240px] sm:h-[240px] md:h-[240px] lg:col-span-3">
                  <EmblaCarousel slides={carouselItems3} border={true} />
                </div>
                <div className="h-[240px] sm:h-[240px] md:col-span-2 lg:col-span-6 lg:h-full lg:max-h-[240px]">
                  <div className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm space-y-4 h-full flex flex-col">
                    <div className="flex justify-between">
                      <div className="grid grid-cols-2 h-full w-full">
                        <div className="flex flex-col">
                          <div className="flex flex-row items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-[#35C1ED]"></div>
                            <span className="text-[#48455B] text-sm">
                              Walkout
                            </span>
                          </div>
                          <div>
                            <span className="text-black text-xl font-bold">
                              {formatCurrency(
                                calculateRunFigures(
                                  selectedRun,
                                  showsForRun(selectedShows, selectedRun)
                                ).walkoutForecast
                              )}
                            </span>
                          </div>
                          <div className="hidden">
                            <span className="text-[#48455B] text-sm">
                              increase/decrease
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <div className="flex flex-row items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-[#8B5CF6]"></div>
                            <span className="text-[#48455B] text-sm">
                              Tickets sold
                            </span>
                          </div>
                          <div>
                            <span className="text-black text-xl font-bold">
                              {formatNumber(
                                runs
                                  ?.flatMap((r) =>
                                    showsForRun(selectedShows, r)
                                  )
                                  .reduce(
                                    (total, show) =>
                                      total +
                                      (calculateShowFigures(show)
                                        .ticketsSoldActual ?? 0),
                                    0
                                  ) ?? 0
                              )}
                            </span>
                          </div>
                          <div className="hidden">
                            <span className="text-[#48455B] text-sm">
                              increase/decrease
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="h-full">
                      <Line data={chartData} options={chartOptions} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200 pt-2">
                {TABS.map((tab) => (
                  <button
                    type="button"
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      px-4 
                      pt-2
                      mt-2
                      text-[#48455B] 
                      border-b-2 
                      transition-colors 
                      duration-200 
                      hover:text-[#6D53FD] 
                      ${
                        activeTab === tab.id
                          ? "border-[#6D53FD] text-[#6D53FD]"
                          : "border-transparent"
                      }
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div className="pb-16 h-96 overflow-y-auto overflow-x-hidden">
                {activeTab === "shows" && (
                  <table className="min-w-full divide-y divide-[#D9D8E1]">
                    <thead className="sticky top-0 z-10 bg-white pt-6 border-b">
                      <tr>
                        {TABLE_HEADERS.map((header) => (
                          <th key={header} className={`${TABLE_HEADER_STYLES}`}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {selectedShows?.length > 0 ? (
                        selectedShows.map((show) => {
                          const dateDisplay = prettyDate(show.performanceDate);

                          return (
                            <tr
                              key={show.id}
                              className="hover:bg-gray-50 cursor-pointer transition-all duration-150"
                              onClick={() =>
                                navigate(
                                  `/runs/${selectedRun.id}/shows/${show.id}`
                                )
                              }
                              onAuxClick={(e) => {
                                e.preventDefault();
                                window.open(
                                  `/runs/${selectedRun.id}/shows/${show.id}`,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              }}
                            >
                              <td className="w-[7%] px-4 py-1">
                                <div className="flex flex-col items-center justify-center border rounded-xl w-[56px] px-8 py-2 text-sm">
                                  {show.performanceDate ? (
                                    <>
                                      <span className="text-xl font-bold text-black leading-none">
                                        {dateDisplay.day}
                                      </span>
                                      <span className="text-sm text-[#48455B] mt-1">
                                        {dateDisplay.month}
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-xl font-bold text-black leading-none">
                                      TBD
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="w-[21%] px-4">
                                <div className="flex flex-col items-start">
                                  <span className="text-md font-semibold text-black">
                                    {show.venue ?? "Venue: TBD"}
                                  </span>
                                  <span className="text-sm text-[#48455B]">
                                    {show.city ?? "TBD"}, {show.state ?? "TBD"},
                                    USA
                                  </span>
                                </div>
                              </td>

                              <td className="w-[44%] px-4">
                                <div className="flex items-center gap-4 w-full">
                                  <div className="w-3/4">
                                    <StatusStepper
                                      currentStatus={show.status}
                                    />
                                  </div>
                                  <div className="w-1/4">
                                    <div
                                      className={`px-2 rounded-lg inline-flex items-center ${getStatusBackgroundColor(
                                        show.status
                                      )}`}
                                    >
                                      <span
                                        className={`text-sm font-semibold whitespace-nowrap ${getStatusTextColor(
                                          show.status
                                        )}`}
                                      >
                                        {show.status ?? "TBD"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="w-[14%] px-4">
                                <div className="flex flex-col items-start">
                                  <span className="text-[#48455B]">
                                    {calculateTicketsSoldForecast(show)}/
                                    {calculateSellableCap(show)}
                                  </span>
                                </div>
                              </td>
                              <td className="w-[14%] px-4">
                                <div className="flex flex-col items-start">
                                  <span
                                    className={getStatusStyling(show.status)}
                                  >
                                    {formatCurrency(
                                      isShowInActualStatus(show)
                                        ? calculateShowFigures(show).walkout
                                        : calculateShowFigures(show)
                                            .walkoutProjected
                                    )}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-6 py-4 text-center text-gray-500"
                          >
                            No shows available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                {activeTab === "runFinances" && (
                  <>
                    <div className="flex flex-col px-4 pt-2">
                      <SummaryTable
                        title="Run Overview"
                        accordionsList={runDetailsAccordions()}
                        estimatedTitle="Forecast"
                      />
                      <FinancesTable
                        title="Crew"
                        data={values?.crew || []}
                        columns={[
                          {
                            title: "Name",
                            fieldname: "name",
                            type: "text",
                          },
                          { title: "Note", fieldname: "notes", type: "text" },
                          {
                            title: "Estimated Quantity",
                            fieldname: "quantity",
                            type: "number",
                          },
                          {
                            title: "Budget",
                            fieldname: "cost",
                            type: "currency",
                          },
                          {
                            title: "Actual",
                            fieldname: "actualCost",
                            type: "currency",
                          },
                        ]}
                        estimatedColumnName="Budget"
                        fieldArrayName="crew"
                        blankObjectValue={{
                          quantity: 1,
                          cost: 0,
                          actualCost: null,
                          name: "",
                          tag: ArtistExpenseTag.CREW,
                        }}
                        calculateEstimated={(row) => row.quantity * row.cost}
                        calculateActual={(row) => row.actualCost}
                        calculateDifference={(data) => {
                          if (!data.actualCost || !data.cost) {
                            return 0;
                          }
                          return data.actualCost - data.cost * data.quantity;
                        }}
                        invertColors={true}
                      />
                      <FinancesTable
                        title="Artist Expenses"
                        data={values?.expenses || []}
                        columns={[
                          { title: "Name", fieldname: "name", type: "text" },
                          {
                            title: "Tag",
                            fieldname: "tag",
                            type: "select",
                            selectOptions: enumToOptions(ArtistExpenseTag),
                          },
                          { title: "Note", fieldname: "notes", type: "text" },
                          {
                            title: "Estimated Quantity",
                            fieldname: "quantity",
                            type: "number",
                          },
                          {
                            title: "Budget",
                            fieldname: "cost",
                            type: "currency",
                          },
                          {
                            title: "Actual",
                            fieldname: "actualCost",
                            type: "currency",
                          },
                        ]}
                        estimatedColumnName="Budget"
                        fieldArrayName="expenses"
                        blankObjectValue={{
                          quantity: 1,
                          cost: 0,
                          actualCost: null,
                          name: "",
                          notes: "",
                          tag: ArtistExpenseTag.MISC,
                        }}
                        calculateEstimated={(row) => row.quantity * row.cost}
                        calculateActual={(row) => row.actualCost}
                        calculateDifference={(data) => {
                          if (!data.actualCost || !data.cost) {
                            return 0;
                          }
                          return data.actualCost - data.cost * data.quantity;
                        }}
                        invertColors={true}
                      />
                    </div>
                  </>
                )}
                {activeTab === "runDocuments" && (
                  <>
                    <DocumentsTable
                      files={showsForRun(selectedShows, selectedRun).flatMap(
                        (show) => show.files
                      )}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </ReusableFormikWrapper>
  );
};
